import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MAX_TIME_WINDOWS } from './ScheduleInterview.consts';
import { IScheduleInterviewForm } from './ScheduleInterview.interfaces';
import {
    ITimeWindow,
    IInterviewAvailability,
    IScheduleInterviewData,
} from '../../../../common';
import {
    generateDurationOptions,
    generateTimeOptions,
} from './ScheduleInterview.utils';

export const useScheduleInterviewForm = (
    onSubmit?: (data: IScheduleInterviewData) => void
) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    
    const durationOptions = useMemo(
        () => generateDurationOptions(t, language),
        [t, language]
    );
    
    const timeOptions = useMemo(
        () => generateTimeOptions(language),
        [language]
    );
    const [selectedDay, setSelectedDay] = useState<number>(1);
    const [availability, setAvailability] = useState<{
        [key: number]: ITimeWindow[];
    }>({});
    const [errors, setErrors] = useState<{
        [key: string]: string;
    }>({});

    const { control, watch, handleSubmit } = useForm<IScheduleInterviewForm>({
        defaultValues: {
            interviewDuration: null,
            availability: {},
        },
    });

    const interviewDuration = watch('interviewDuration');
    const isDurationSelected = interviewDuration !== null;
    const currentDayWindows = availability[selectedDay] || [];
    const canAddMore = currentDayWindows.length < MAX_TIME_WINDOWS;

    // Initialize Monday when duration is selected
    useEffect(() => {
        if (isDurationSelected && Object.keys(availability).length === 0) {
            const mondayWindows = [
                {
                    id: Date.now().toString(),
                    from: '',
                    to: '',
                },
            ];
            setAvailability({
                1: mondayWindows,
            });
            setSelectedDay(1);
        }
    }, [isDurationSelected, availability]);

    const handleDayClick = (dayValue: number) => {
        if (!isDurationSelected) return;

        setSelectedDay(dayValue);
        if (!availability[dayValue]) {
            const newWindows = [
                {
                    id: Date.now().toString(),
                    from: '',
                    to: '',
                },
            ];
            setAvailability({
                ...availability,
                [dayValue]: newWindows,
            });
        }
    };

    const handleAddTimeWindow = () => {
        const currentWindows = availability[selectedDay] || [];
        if (currentWindows.length < MAX_TIME_WINDOWS) {
            const newWindow: ITimeWindow = {
                id: Date.now().toString(),
                from: '',
                to: '',
            };
            setAvailability({
                ...availability,
                [selectedDay]: [...currentWindows, newWindow],
            });
        }
    };

    const handleRemoveTimeWindow = (windowId: string) => {
        const currentWindows = availability[selectedDay] || [];
        const updatedWindows = currentWindows.filter((w) => w.id !== windowId);

        if (updatedWindows.length === 0) {
            const newAvailability = { ...availability };
            delete newAvailability[selectedDay];
            setAvailability(newAvailability);
        } else {
            setAvailability({
                ...availability,
                [selectedDay]: updatedWindows,
            });
        }
    };

    const handleTimeWindowChange = (
        windowId: string,
        field: 'from' | 'to',
        value: string
    ) => {
        const currentWindows = availability[selectedDay] || [];
        const updatedWindows = currentWindows.map((window) => {
            if (window.id === windowId) {
                return { ...window, [field]: value };
            }
            return window;
        });
        setAvailability({
            ...availability,
            [selectedDay]: updatedWindows,
        });
        
        // Clear error for this field
        const errorKey = `${windowId}_${field}`;
        if (errors[errorKey]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    const getFilteredTimeOptions = (windowId: string, field: 'from' | 'to') => {
        const currentWindows = availability[selectedDay] || [];
        const currentWindow = currentWindows.find((w) => w.id === windowId);

        if (!currentWindow || !interviewDuration) return timeOptions;

        const durationMinutes = parseInt(interviewDuration.value);

        if (field === 'to') {
            // If "from" is not set, return all options
            if (!currentWindow.from) return timeOptions;
            
            const fromMinutes = parseInt(currentWindow.from);
            return timeOptions.filter((option) => {
                const toMinutes = parseInt(option.value);
                return (
                    toMinutes > fromMinutes &&
                    toMinutes - fromMinutes >= durationMinutes
                );
            });
        } else {
            // If "to" is not set, return all options
            if (!currentWindow.to) return timeOptions;
            
            const toMinutes = parseInt(currentWindow.to);
            return timeOptions.filter((option) => {
                const fromMinutes = parseInt(option.value);
                return (
                    fromMinutes < toMinutes &&
                    toMinutes - fromMinutes >= durationMinutes
                );
            });
        }
    };

    const checkOverlap = (windows: ITimeWindow[]): boolean => {
        for (let i = 0; i < windows.length; i++) {
            for (let j = i + 1; j < windows.length; j++) {
                const window1From = parseInt(windows[i].from);
                const window1To = parseInt(windows[i].to);
                const window2From = parseInt(windows[j].from);
                const window2To = parseInt(windows[j].to);

                if (
                    (window1From < window2To && window1To > window2From) ||
                    (window2From < window1To && window2To > window1From)
                ) {
                    return true;
                }
            }
        }
        return false;
    };

    const onFormSubmit = (data: IScheduleInterviewForm) => {
        const newErrors: { [key: string]: string } = {};
        let firstErrorDay: number | null = null;

        // Build cleaned availability: skip fully empty windows (both from and to blank)
        const cleanedAvailability: IInterviewAvailability = {};
        for (const day in availability) {
            const dayIndex = parseInt(day);
            const windows = availability[dayIndex];
            const activeWindows = windows.filter((w) => w.from || w.to);
            if (activeWindows.length > 0) {
                cleanedAvailability[dayIndex] = activeWindows;
            }
        }

        for (const day in cleanedAvailability) {
            const dayIndex = parseInt(day);
            const windows = cleanedAvailability[dayIndex];

            for (const window of windows) {
                if (!window.from) {
                    newErrors[`${window.id}_from`] = t(
                        'scheduleInterview.pleaseSelectTime'
                    );
                    firstErrorDay ??= dayIndex;
                }
                if (!window.to) {
                    newErrors[`${window.id}_to`] = t(
                        'scheduleInterview.pleaseSelectTime'
                    );
                    firstErrorDay ??= dayIndex;
                }
            }

            const allFilled = windows.every((w) => w.from && w.to);
            if (allFilled && checkOverlap(windows)) {
                windows.forEach((window) => {
                    newErrors[`${window.id}_overlap`] = t(
                        'scheduleInterview.overlappingWindows'
                    );
                });
                firstErrorDay ??= dayIndex;
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            if (firstErrorDay !== null && firstErrorDay !== selectedDay) {
                setSelectedDay(firstErrorDay);
            }
            return;
        }

        if (
            Object.keys(cleanedAvailability).length === 0 ||
            !data.interviewDuration
        ) {
            return;
        }

        onSubmit?.({
            interviewDuration: data.interviewDuration,
            availability: cleanedAvailability,
        });
    };

    const isDayComplete = (dayValue: number): boolean => {
        const windows = availability[dayValue];
        if (!windows || windows.length === 0) return false;
        return windows.some(w => w.from && w.to);
    };

    return {
        control,
        selectedDay,
        availability,
        interviewDuration,
        isDurationSelected,
        currentDayWindows,
        canAddMore,
        errors,
        durationOptions,
        timeOptions,
        language,
        handleDayClick,
        handleAddTimeWindow,
        handleRemoveTimeWindow,
        handleTimeWindowChange,
        getFilteredTimeOptions,
        handleSubmit: handleSubmit(onFormSubmit),
        isDayComplete,
    };
};
